import { PARTICLE_SIZE, type ParticleMotion, type Weather } from '../../shared/weather.ts';
import type { SpriteRef } from '../../shared/map-elements.ts';
import { tilesetImage } from '../common/element-renderer.ts';

/** Nombre de particules pour un écran de 1000 × 600 px, à intensité maximale. */
const DENSITY = { rain: 520, snow: 340, fog: 55 } as const;
const AREA = 1000 * 600;
/** Voile qui assombrit l'écran, à intensité maximale (voir Weather.overcast). */
const OVERCAST = { rain: [40, 60, 90, 0.25], snow: [200, 215, 240, 0.16], fog: [200, 205, 215, 0.15] } as const;

/** Valeur pseudo-aléatoire fixe d'une particule (0 à 1) : les particules ne sont pas stockées, elles se recalculent. */
const hash = (n: number): number => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/** Reste positif (les particules repassent par l'autre bord). */
const wrap = (value: number, size: number): number => ((value % size) + size) % size;

/**
 * Pluie, neige ou brouillard par-dessus la carte, ou les images choisies pour la météo. Chaque particule a une place
 * fixe dans le monde, déduite de son numéro et de l'heure : elles défilent avec la caméra quand on marche, sans rien
 * garder d'une image à l'autre. `tileSize` : taille d'une case à l'écran (les images et leur mouvement s'y règlent).
 */
export function drawWeather(ctx: CanvasRenderingContext2D, weather: Weather, width: number, height: number, camera: { x: number; y: number }, now: number, tileSize: number): void {
  if (weather.kind === 'none' || weather.intensity <= 0) return;
  const t = now / 1000;
  const area = (width * height) / AREA;
  ctx.save();
  if (weather.overcast) {
    const [r, g, b, alpha] = OVERCAST[weather.kind];
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * weather.intensity})`;
    ctx.fillRect(0, 0, width, height);
  }
  if (weather.particles.length) {
    drawParticles(ctx, weather.particles, weather.motion, weather.intensity, width, height, camera, t, area, tileSize);
  } else if (weather.kind === 'rain') {
    // Des traits obliques qui tombent vite.
    const count = Math.round(DENSITY.rain * weather.intensity * area);
    ctx.strokeStyle = 'rgba(200, 222, 255, 0.75)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < count; i++) {
      const speed = 700 + hash(i) * 300;
      const x = wrap(hash(i + 0.5) * width * 1.2 - camera.x - t * speed * 0.25, width + 40) - 20;
      const y = wrap(hash(i + 0.25) * height - camera.y + t * speed, height + 40) - 20;
      ctx.moveTo(x, y);
      ctx.lineTo(x - 5, y + 20);
    }
    ctx.stroke();
  } else if (weather.kind === 'snow') {
    const count = Math.round(DENSITY.snow * weather.intensity * area);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    for (let i = 0; i < count; i++) {
      const speed = 30 + hash(i) * 40;
      const size = 3 + hash(i + 0.75) * 3;
      const sway = Math.sin(t * (0.6 + hash(i + 0.1)) + i) * 18;
      const x = wrap(hash(i + 0.5) * width - camera.x + sway, width + 20) - 10;
      const y = wrap(hash(i + 0.25) * height - camera.y + t * speed, height + 20) - 10;
      ctx.fillRect(Math.round(x), Math.round(y), Math.round(size), Math.round(size));
    }
  } else {
    drawFog(ctx, weather.intensity, width, height, camera, t, area);
  }
  ctx.restore();
}

// ---------- Images choisies ----------

const TURN = Math.PI * 2;

/**
 * Les images de la météo, chacune tirée au hasard parmi celles choisies, qui tombent selon les réglages du mouvement :
 * vitesse et direction, balancement, oscillation, rotation et retournement, chaque particule à son rythme.
 */
function drawParticles(
  ctx: CanvasRenderingContext2D, sprites: readonly SpriteRef[], motion: ParticleMotion, intensity: number,
  width: number, height: number, camera: { x: number; y: number }, t: number, area: number, tileSize: number,
): void {
  const images = sprites.map((sprite) => ({ sprite, image: tilesetImage(sprite.image) }));
  const count = Math.round(motion.density * intensity * area);
  const heading = (motion.direction * Math.PI) / 180;
  const along = { x: Math.sin(heading), y: Math.cos(heading) };
  const wobble = (motion.wobble * Math.PI) / 180;
  const margin = tileSize * (motion.size * 2 + motion.sway * 2 + 1);
  // Transformation de départ (densité de pixels de l'écran), reprise pour chaque image.
  const base = ctx.getTransform();
  ctx.imageSmoothingEnabled = false;
  for (let i = 0; i < count; i++) {
    const { sprite, image } = images[Math.floor(hash(i + 0.33) * images.length)]!;
    if (!image.complete || !image.naturalWidth) continue;
    // Écart de chaque particule autour des valeurs moyennes (de 1 - variation/2 à 1 + variation/2).
    const vary = (seed: number) => 1 + (hash(i + seed) - 0.5) * motion.variation;
    const size = tileSize * motion.size * (0.78 + hash(i + 0.75) * 0.44);
    const phase = hash(i + 0.6) * TURN;
    const travel = t * motion.speed * tileSize * vary(0);
    // Le balancement se fait en travers de la chute.
    const sway = Math.sin(t * (0.6 + hash(i + 0.1) * 0.8) + phase) * motion.sway * tileSize;
    const dx = along.x * travel + along.y * sway;
    const dy = along.y * travel - along.x * sway;
    const spin = t * motion.spin * TURN * vary(0.2) * (hash(i + 0.4) < 0.5 ? -1 : 1);
    const angle = (motion.align ? -heading : 0) + spin + Math.sin(t * (0.8 + hash(i + 0.2)) + phase) * wobble + (motion.spin ? phase : 0);
    // En se retournant, l'image s'amincit puis montre son autre face.
    const flip = motion.tumble ? Math.cos(t * motion.tumble * TURN * vary(0.8) + phase) : 1;
    const x = wrap(hash(i + 0.5) * (width + margin) - camera.x + dx, width + margin) - margin / 2;
    const y = wrap(hash(i + 0.25) * (height + margin) - camera.y + dy, height + margin) - margin / 2;
    ctx.setTransform(base);
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.scale(Math.abs(flip) < 0.15 ? 0.15 * Math.sign(flip || 1) : flip, 1);
    ctx.drawImage(image, sprite.x, sprite.y, PARTICLE_SIZE, PARTICLE_SIZE, -size / 2, -size / 2, size, size);
  }
  ctx.setTransform(base);
}

// ---------- Brouillard ----------

/** Variantes de nappes de brume, dessinées une fois (des dégradés à chaque image coûteraient trop cher). */
const WISP_VARIANTS = 4;
const WISP_SIZE = { width: 220, height: 80 };
let wispSprites: HTMLCanvasElement[] | null = null;

/** Une nappe : quelques taches floues qui se chevauchent, plus denses au centre, en forme de traînée allongée. */
function wispSprite(variant: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = WISP_SIZE.width;
  canvas.height = WISP_SIZE.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  // Taches aplaties (étirées en largeur), légères chacune : c'est leur chevauchement qui fait le cœur de la nappe.
  ctx.scale(1, 0.6);
  const puffs = 10 + Math.floor(hash(variant * 13.7) * 5);
  for (let i = 0; i < puffs; i++) {
    const seed = variant * 31 + i;
    const x = WISP_SIZE.width * (0.2 + hash(seed + 0.1) * 0.6);
    const y = (WISP_SIZE.height / 0.6) * (0.36 + hash(seed + 0.2) * 0.28);
    const radius = WISP_SIZE.height * (0.35 + hash(seed + 0.3) * 0.35);
    const puff = ctx.createRadialGradient(x, y, 0, x, y, radius);
    puff.addColorStop(0, 'rgba(236, 239, 245, 0.16)');
    puff.addColorStop(0.6, 'rgba(236, 239, 245, 0.07)');
    puff.addColorStop(1, 'rgba(236, 239, 245, 0)');
    ctx.fillStyle = puff;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  return canvas;
}

/**
 * Brouillard : de petites nappes de brume posées dans le monde qui dérivent doucement avec le vent,
 * ondulent, et s'épaississent ou se dissipent chacune à son rythme. L'intensité règle leur nombre et leur opacité.
 */
function drawFog(ctx: CanvasRenderingContext2D, intensity: number, width: number, height: number, camera: { x: number; y: number }, t: number, area: number): void {
  wispSprites ??= Array.from({ length: WISP_VARIANTS }, (_, variant) => wispSprite(variant));
  const count = Math.max(4, Math.round(DENSITY.fog * area * (0.35 + 0.65 * intensity)));
  for (let i = 0; i < count; i++) {
    const scale = 0.8 + hash(i + 0.15) * 1.2;
    const w = WISP_SIZE.width * scale, h = WISP_SIZE.height * scale;
    const phase = hash(i + 0.6) * Math.PI * 2;
    // Dérive lente dans le sens du vent, avec un léger va-et-vient ; presque collée au sol (elle suit la caméra à 90 %).
    const drift = t * (3 + hash(i + 0.3) * 7) + Math.sin(t * (0.07 + hash(i + 0.4) * 0.08) + phase) * 10;
    const sway = Math.sin(t * (0.1 + hash(i + 0.45) * 0.12) + phase) * 6;
    const x = wrap(hash(i + 0.5) * (width + w) - camera.x * 0.9 + drift, width + w) - w;
    const y = wrap(hash(i + 0.25) * (height + h) - camera.y * 0.9 + sway, height + h) - h;
    // Chaque nappe s'épaissit et se dissipe lentement ; certaines restent plus légères que d'autres.
    const breath = 0.5 + 0.5 * Math.sin(t * (0.06 + hash(i + 0.7) * 0.1) + phase * 1.7);
    const alpha = intensity * (0.3 + hash(i + 0.9) * 0.6) * (0.25 + 0.75 * breath);
    if (alpha < 0.02) continue;
    ctx.globalAlpha = Math.min(1, alpha * 1.8);
    ctx.drawImage(wispSprites[i % WISP_VARIANTS]!, x, y, w, h);
  }
  ctx.globalAlpha = 1;
}
